import posthog from "posthog-js";

/**
 * PostHog bootstrap for the web app.
 *
 * The client is initialised once at app start (see App.tsx). Everything else
 * goes through `shared/analytics.ts`, which fans events out to PostHog and
 * Google Analytics so call sites never talk to a vendor SDK directly.
 */
const apiKey = process.env.REACT_APP_POSTHOG_KEY;
const apiHost =
  process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

let initialised = false;

export const isPostHogConfigured = (): boolean =>
  Boolean(apiKey) &&
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "test";

export const initPostHog = (): void => {
  if (initialised || !isPostHogConfigured()) {
    return;
  }

  posthog.init(apiKey as string, {
    api_host: apiHost,
    // Page views are captured from the router (usePageTracking) so SPA
    // navigations are tracked, not just the initial document load.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    capture_dead_clicks: true,
    rageclick: true,
    // Only create person profiles once a visitor signs up / logs in. Anonymous
    // homepage traffic is still captured and merged on identify.
    person_profiles: "identified_only",
    session_recording: {
      maskAllInputs: true,
    },
    disable_session_recording:
      process.env.REACT_APP_POSTHOG_SESSION_REPLAY === "false",
    debug: process.env.NODE_ENV === "development",
  });

  initialised = true;
};

/** Returns the client only when it has been initialised. */
export const getPostHog = (): typeof posthog | null =>
  initialised ? posthog : null;

/** Test helper so analytics unit tests can inject a fake client. */
export const __setPostHogInitialisedForTests = (value: boolean): void => {
  initialised = value;
};
